import type { ReactElement } from 'react';
import { Suspense, useState } from 'react';

import BpkButton, { BUTTON_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-button';
import { persistUserSessionData } from '@skyscanner-internal/global-components/culture-selector/helpers';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { getTrustedFunnel } from '@web-platform/shared-universal-redux/src/state';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { lazyWithRetry } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { getFooterData } from '@web-platform/shared-universal-utils-frontend/src/singletons/footerData';
import { useSelector } from 'react-redux';

import type {
  CultureSelectorCurrency,
  CultureSelectorLocale,
  CultureSelectorMarket,
} from '@skyscanner-internal/global-components/culture-selector/CultureSelector';
import type { OnCultureSelectorButtonClick } from '@skyscanner-internal/global-components/culture-selector/helpers/useCultureSelectorData';

import STYLES from './CultureSelectorButton.module.scss';

const LazyCultureSelectorModal = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CultureSelectorModal" */
      '../../CultureSelectorModal/CultureSelectorModal'
    ),
);

type CultureSelectorButtonProps = {
  currencies: CultureSelectorCurrency[];
  markets: CultureSelectorMarket[];
  enMarkets: CultureSelectorMarket[];
  locales: CultureSelectorLocale[];
  isFetching: boolean;
  onCultureSelectorButtonClick: OnCultureSelectorButtonClick;
};

const CultureSelectorButton = ({
  currencies,
  enMarkets,
  isFetching,
  locales,
  markets,
  onCultureSelectorButtonClick,
}: CultureSelectorButtonProps): ReactElement => {
  const [isCultureSelectorOpen, setIsCultureSelectorOpen] = useState(false);

  const { currentScreenName } = useSelector(getTrustedFunnel);

  const enableAnonymousUtidPropagation = getFeatureDecision(
    FEATURE_DECISION_KEYS.ANONYMOUS_UTID_PROPAGATION_EXPLICIT_TLD_REDIRECT,
  );

  let currencyData;

  let country;

  let languageShort;

  let language;

  const footerData = getFooterData();

  if (footerData && footerData.cultureData) {
    const { cultureData } = footerData;

    languageShort = cultureData.localeNativeShortName;
    language = cultureData.localeNativeName;
    country = cultureData.marketTranslatedName;
    currencyData = cultureData.currencyData;
  } else {
    // Culture data singleton not yet initialised.
    currencyData = {
      symbol: '£',
      currency: 'GBP',
      name: 'British Pound',
    };
    country = 'United Kingdom';
    languageShort = 'English (UK)';
    language = 'English (United Kingdom)';
  }

  const currencyString = (
    <>
      <span>{currencyData.symbol}&nbsp;</span>
      <span>{currencyData.currency}</span>
    </>
  );
  const cultureButtonItemSeparator = <span className={STYLES.cultureSelectorSeparator}>·</span>;
  const currencyElement = <span className={STYLES.currencyLabel}>{currencyString}</span>;

  return (
    <>
      <BpkButton
        type={BUTTON_TYPES.secondaryOnDark}
        onClick={(e) => {
          e.preventDefault();
          trackInteractionEvent({
            name: ANALYTICS_EVENTS.INTERACTION.CULTURE_SELECTOR_TAPPED.NAME,
            strings: {
              screenName: currentScreenName,
            },
          });
          setIsCultureSelectorOpen((prevState) => !prevState);
          onCultureSelectorButtonClick()
            .then(() => {
              setIsCultureSelectorOpen(true);
            })
            .catch((error) => {
              logError({
                component: 'CultureSelectorButton',
                error,
                message: `Error with CultureSelectorButtonClick`,
              });
              setIsCultureSelectorOpen(false);
            });
        }}
        className={STYLES.button}
        aria-label={translate('aria_culture_selector_button', {
          language,
          currency: currencyData.name,
        })}
        fullWidth
      >
        <span className={STYLES.cultureSelectorText}>
          {country}
          {cultureButtonItemSeparator}
          {languageShort}
          {cultureButtonItemSeparator}
          {currencyElement}
        </span>
      </BpkButton>
      <Suspense fallback={null}>
        <LazyCultureSelectorModal
          currencies={currencies}
          markets={markets}
          enMarkets={enMarkets}
          locales={locales}
          isOpen={isCultureSelectorOpen && !isFetching}
          onClose={() => setIsCultureSelectorOpen((prevState) => !prevState)}
          persistUserSessionDataFn={
            enableAnonymousUtidPropagation ? persistUserSessionData : undefined
          }
        />
      </Suspense>
    </>
  );
};

export default CultureSelectorButton;
